import React, {
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { Close, Check, ArrowBack } from '@material-ui/icons';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { t, canURL } from '~/helpers';

import * as S from './styles';

export default function ModalFormAux({
  component: Component,
  maxWidth,
  fullScreen,
  loading,
  create,
  btnConfirm,
  btnCancell,
  onClose,
}) {
  const formRef = useRef(null);

  const [data, setData] = useState({});
  const [showButton, setShowButton] = useState(true);
  const [innerLoading, setInnerLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { opened } = useSelector((state) => state.dialogForm);
  const { title } = useSelector((state) => state.drawer);
  const isFormLoading = innerLoading && !loading && !hasSubmitted;

  const handleSubmit = useCallback(() => {
    if (formRef.current) {
      setHasSubmitted(true);
      setInnerLoading(false);
      setData(formRef.current.getData());

      formRef.current.submitForm();
    }
  }, []);

  const handleCloseForm = useCallback(() => {
    setData({});
    setInnerLoading(false);
    setHasSubmitted(false);

    if (formRef.current) {
      formRef.current.setData({});
      formRef.current.reset();
    }

    onClose();
  }, []);

  const handleToggleButton = useCallback(
    (value) => {
      setShowButton(value);
    },
    [loading]
  );

  const buttons = useMemo(() => {
    if (isEqual(canURL(), { read: true })) {
      return (
        <Button
          startIcon={<ArrowBack />}
          color="secondary"
          variant="outlined"
          size="small"
          onClick={handleCloseForm}
        >
          {t('s.geral.botaovoltar')}
        </Button>
      );
    }

    return (
      <>
        {showButton && (
          <Button
            startIcon={<Check />}
            color="primary"
            variant="outlined"
            size="small"
            onClick={() => {
              // console.log('asd');
              handleSubmit();
            }}
            disabled={loading || isFormLoading}
          >
            {t(btnConfirm)}
          </Button>
        )}

        <Button
          startIcon={<Close />}
          color="secondary"
          variant="outlined"
          size="small"
          onClick={handleCloseForm}
          disabled={loading || isFormLoading}
        >
          {showButton ? t(btnCancell) : t('s.geral.botaofechar')}
        </Button>
      </>
    );
  }, [showButton, loading, isFormLoading, btnConfirm, handleCloseForm]);

  useEffect(() => {
    if (!opened) {
      setData({});
      setHasSubmitted(false);

      if (formRef.current) {
        formRef.current.reset();
      }
    }
  }, [opened]);

  return (
    <Dialog
      open={opened}
      onClose={handleCloseForm}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      disableBackdropClick
      disableEscapeKeyDown
      fullWidth
    >
      {(loading || isFormLoading) && <LinearProgress />}
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <S.ContentWrapper>
          {isFormLoading && (
            <S.LoadingOverlay>
              <S.LoadingContent>
                <CircularProgress />
                <Typography variant="body2" color="textSecondary">
                  Carregando...
                </Typography>
              </S.LoadingContent>
            </S.LoadingOverlay>
          )}
          <S.WrapperForm>
            {Component && (
              <Component
                reference={formRef}
                create={create}
                data={data}
                toggleConfirmButton={handleToggleButton}
                toggleLoading={setInnerLoading}
              />
            )}
          </S.WrapperForm>
        </S.ContentWrapper>
      </DialogContent>
      <DialogActions>{buttons}</DialogActions>
    </Dialog>
  );
}

ModalFormAux.propTypes = {
  component: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
    .isRequired,
  maxWidth: PropTypes.string.isRequired,
  fullScreen: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  create: PropTypes.bool.isRequired,
  btnConfirm: PropTypes.string.isRequired,
  btnCancell: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};
